import { useState, useEffect } from 'react';
import { useSubscriptionStore } from '@/features/auth/stores/subscription.store';
import { useSettingsStore } from '@/features/settings/stores/settings.store';
import { AlertCircle, Phone, Mail } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { NotificationsSection } from '@/features/settings/components/NotificationsSection';
import { CustomDomainSettings } from '@/features/domains/components/CustomDomainSettings';
import { CrmConnectionSection } from '@/features/settings/components/CrmConnectionSection';
import { DocumentUploadSettings } from '@/features/settings/components/DocumentUploadSettings';
import { BankStatementAnalysisSettings } from '@/features/settings/components/BankStatementAnalysisSettings';
import { DeleteAccountSection } from '@/features/settings/components/DeleteAccountSection';
import { useDashboardStore } from '@/features/dashboard/stores/dashboard.store';
import { logger } from '@/features/help/utils/logger';
import { CancelSubscriptionModal } from './CancelSubscriptionModal';
import { SupportImpersonationSection } from '@/features/support/components/SupportImpersonationSection';
import { SenderDomainSettings } from '@/features/email/components/SenderDomainSettings';

/**
 * Formats a price amount from cents to a currency string
 */
const formatPrice = (amount: number, currency: string = 'usd'): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  });

  return formatter.format(amount / 100);
};

export const Settings = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isCancelConfirmModalOpen, setIsCancelConfirmModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const {
    checkSubscription,
    isSubscribed,
    status,
    currentPeriodEnd,
    freeTrialLimit,
    freeTrialRemaining,
    freeUsageCount,
    usageCount,
    standardApplicationPrice,
    createSubscription,
    cancelSubscription,
  } = useSubscriptionStore();

  const { fetchSettings } = useSettingsStore();

  const markSettingsViewed = useDashboardStore((state) => state.markSettingsViewed);

  useEffect(() => {
    fetchSettings().catch((err) => {
      logger.error('SettingsPage', 'Error fetching settings on mount', { error: err });
    });
  }, [fetchSettings]);

  useEffect(() => {
    checkSubscription();
    markSettingsViewed();
  }, [checkSubscription, markSettingsViewed]);

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };

  // Handle direct subscription creation
  const handleUpgrade = async () => {
    try {
      setIsProcessing(true);
      const checkoutUrl = await createSubscription();
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Failed to create subscription. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Function to execute the actual cancellation logic (called by the modal)
  const executeCancellation = async () => {
    logger.info('SettingsPage', 'Executing subscription cancellation after modal confirmation');
    setIsCancelConfirmModalOpen(false); // Close modal first
    setIsProcessing(true); // Show processing state on the button
    const success = await cancelSubscription(); // Call the store action
    setIsProcessing(false); // Hide processing state

    if (success) {
      logger.info(
        'SettingsPage',
        'Subscription cancellation process completed successfully via store action'
      );
      // UI updates handled by store reaction
    } else {
      logger.error('SettingsPage', 'Subscription cancellation process failed via store action');
      // Error toast handled by store action
    }
  };

  // Get the formatted standard application price or fallback to default
  const getStandardApplicationPriceDisplay = () => {
    if (standardApplicationPrice?.unit_amount) {
      return formatPrice(standardApplicationPrice.unit_amount, standardApplicationPrice.currency);
    }
    return '$2.50'; // Fallback price if not available from API
  };

  // Trial status computation
  const trialProgress = Math.min(100, (freeUsageCount / freeTrialLimit) * 100);
  const trialProgressColor =
    freeTrialRemaining > 5
      ? 'bg-green-500'
      : freeTrialRemaining > 0
        ? 'bg-yellow-500'
        : 'bg-red-500';

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-bold text-gray-900">Settings</h1>

      {/* Subscription Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Subscription Management</h2>

        {/* Usage section render */}
        <div className="mb-8">
          <h3 className="text-lg font-medium mb-4">Subscription Status</h3>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-600">Status</span>
              <span
                className={`font-medium ${isSubscribed ? 'text-green-600' : freeTrialRemaining > 0 ? 'text-blue-600' : 'text-red-600'}`}
              >
                {isSubscribed ? 'Active' : freeTrialRemaining > 0 ? 'Free Trial' : 'Expired'}
              </span>
            </div>

            {!isSubscribed && (
              <>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600">Free Trial Usage</span>
                  <span className="font-medium">
                    {freeUsageCount} / {freeTrialLimit}
                  </span>
                </div>

                <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
                  <div
                    className={`h-2.5 rounded-full ${trialProgressColor}`}
                    style={{ width: `${trialProgress}%` }}
                  ></div>
                </div>

                <p className="text-sm text-gray-500 mb-4">
                  {freeTrialRemaining > 0
                    ? `You have ${freeTrialRemaining} free submissions remaining.`
                    : 'Your free trial has ended. Upgrade to continue.'}
                </p>
              </>
            )}
          </div>
        </div>

        {isSubscribed ? (
          <div className="space-y-6">
            {/* Subscription Status Banner */}
            {currentPeriodEnd &&
              isSubscribed &&
              (status === 'active_until_period_end' || status === 'cancelled') && (
                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                  <div className="flex gap-2 items-start">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium">Subscription Cancelled</p>
                      <p>
                        {status === 'active_until_period_end'
                          ? `Your pro plan benefits will remain active until ${formatDate(currentPeriodEnd)}. After this date, you'll be moved to the free plan.`
                          : 'Your subscription has ended. Resubscribe to regain access to pro features.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

            {/* Current Usage Display */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <h3 className="font-medium text-gray-900">Current Billing Period Usage</h3>
              <div className="flex justify-between items-center text-sm text-gray-600">
                <div>
                  <span className="font-medium">Applications Processed</span>
                  <p className="text-xs text-gray-500">This billing period</p>
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold text-gray-900">{usageCount || 0}</div>
                  <div className="text-xs text-gray-500">
                    {getStandardApplicationPriceDisplay()} per application
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              {status === 'active_until_period_end' || status === 'cancelled' ? (
                <button
                  onClick={handleUpgrade}
                  disabled={isProcessing}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {isProcessing ? 'Processing...' : 'Resubscribe'}
                </button>
              ) : (
                <button
                  onClick={() => {
                    logger.debug(
                      'SettingsPage',
                      'Cancel Subscription button clicked, opening modal'
                    );
                    setIsCancelConfirmModalOpen(true);
                  }}
                  disabled={isProcessing}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  Cancel Subscription
                </button>
              )}
            </div>

            {/* Contact Information Modal */}
            {isContactModalOpen && (
              <div className="space-y-4">
                <div className="flex items-start gap-3 text-gray-700 bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0 text-blue-600" />
                  <div className="space-y-4">
                    <p className="text-gray-800 font-medium">
                      To cancel your subscription, please contact our support team:
                    </p>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-gray-700">
                        <Phone className="h-4 w-4 text-blue-600" />
                        <a href="tel:+19492827201" className="text-blue-600 hover:text-blue-700">
                          (949) 282-7201
                        </a>
                      </div>

                      <div className="flex items-center gap-2 text-gray-700">
                        <Mail className="h-4 w-4 text-blue-600" />
                        <a
                          href="mailto:dylan@bigtunasolutions.com"
                          className="text-blue-600 hover:text-blue-700"
                        >
                          dylan@bigtunasolutions.com
                        </a>
                      </div>

                      <p className="text-sm text-gray-600 mt-2">
                        Our support hours are Monday - Friday: 9:00 AM - 5:00 PM PST
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => setIsContactModalOpen(false)}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            <button
              onClick={handleUpgrade}
              disabled={isProcessing}
              className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isProcessing ? 'Processing...' : 'Upgrade to Pro'}
            </button>
          </div>
        )}
      </div>

      {/* CRM Connection Section */}
      <CrmConnectionSection />

      {/* Custom Domain Settings Section */}
      <CustomDomainSettings />

      {/* Email Sender Domain Settings Section */}
      <SenderDomainSettings />

      {/* Notification Settings Section */}
      <NotificationsSection />

      {/* Document Upload Settings Section */}
      <DocumentUploadSettings />

      {/* Bank Statement Analysis Settings Section */}
      <BankStatementAnalysisSettings />

      {/* Internal Support Section */}
      <SupportImpersonationSection />

      {/* Delete Account Section */}
      <DeleteAccountSection />

      {/* Render the Cancel Subscription Modal */}
      <CancelSubscriptionModal
        isOpen={isCancelConfirmModalOpen}
        onClose={() => {
          logger.debug('SettingsPage', 'Closing cancel subscription modal');
          setIsCancelConfirmModalOpen(false);
        }}
        onConfirm={executeCancellation}
        isProcessing={isProcessing}
        currentPeriodEnd={currentPeriodEnd}
      />
    </div>
  );
};
